import clsx from 'clsx';
import { ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { ModuleTypes } from '$const';
import { getPathnameByType, ROUTE_PAGE, ROUTE_PAGE_WITH_ANCHOR } from '$routes';
import type { RootState } from '$store';
import Button from '~components/generic/elements/Button';
import RouterLink from '~components/generic/RouterLink';
import { useNavigateToPage } from '~hooks/useNavigateToPage';
import { convertToRawValue } from '~services/data-conversion';
import { entityIsOfType, makeSlugForAnchorEntity } from '~services/entity-helpers';
import { getDataFromInternalUrl } from '~services/location-helpers';
import { selectEntityWithChangesById, selectUrlLanguagePrefix } from '~store/selectors';
import { selectMenuElementById } from '~store/selectors/menu';
import { trimLeadingSlash } from '~utils/string';
import stripHtml from '~utils/stripHtml';

export type LinkProps = {
  label?: string;
  url?: string;
  id?: string;
  className?: string;
  children?: ReactNode;
  pageAlias?: string | undefined;
};

const LinkComponent = ({ label, url, id, className, children, pageAlias }: LinkProps) => {
  const navigateToPage = useNavigateToPage();

  // Always call hooks at the top level, no conditional logic
  const language = useSelector(selectUrlLanguagePrefix);
  let slug: string | undefined;
  let labelState = label;

  // Get data for internal links if id is provided
  if (id && id !== '-- none --') {
    const { id: uuid, type } = getDataFromInternalUrl(id);

    if (uuid && type === ModuleTypes.anchorLink) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const entity = useSelector((state: RootState) => selectEntityWithChangesById(state, uuid));
      if (entity) {
        pageAlias = trimLeadingSlash(entity.attributes.page_path);
        slug = makeSlugForAnchorEntity(entity);
        if (!labelState) {
          if (entityIsOfType(entity, ModuleTypes.anchorLink)) {
            labelState = stripHtml(convertToRawValue(entity.attributes.field_title?.value || ''));
          }
        }
      }
    } else {
      // item is not loaded as entity -> it's a menu element
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const menuElement = useSelector((state: RootState) => selectMenuElementById(state, uuid));
      if (menuElement) {
        // We have to check if the menuElement exists because it could have been deleted
        pageAlias = trimLeadingSlash(menuElement.alias);
        if (!labelState) {
          labelState = menuElement.title;
        }
      }
    }
  }

  const handleOnClick = useCallback(() => {
    if (pageAlias) {
      navigateToPage(slug ? ROUTE_PAGE_WITH_ANCHOR : ROUTE_PAGE, { pageAlias, slug });
    }
  }, [navigateToPage, pageAlias, slug]);

  if (id && children) {
    // Internal Link
    return (
      <RouterLink
        to={{
          pathname: pageAlias
            ? getPathnameByType(slug ? ROUTE_PAGE_WITH_ANCHOR : ROUTE_PAGE, { lang: language, pageAlias, slug })
            : '',
        }}
        className={className}
      >
        {children}
      </RouterLink>
    );
  } else if (id) {
    // Internal Link but as Button
    return (
      <Button
        className={clsx('c-button c-button--dark u-margin-top-lg', className)}
        label={labelState}
        onClick={handleOnClick}
      />
    );
  } else if (children) {
    // External Link
    return (
      <RouterLink className={className} target="_blank" rel="noopener noreferrer" to={url}>
        {children}
      </RouterLink>
    );
  } else {
    // Button
    return (
      <Button
        className={clsx('c-button c-button--dark u-margin-top-lg', className)}
        href={url}
        target={url.substring(0, 4).toLowerCase() === 'http' ? '_blank' : '_self'}
        rel="noopener noreferrer"
        label={label || url}
      />
    );
  }
};

export const Link = LinkComponent;
export default Link;
